'use client';

import Link from 'next/link';
import Card from '@/components/layout/Card';
import Icon from '@/components/ui/AppIcon';

export interface TrainingCardProps {
  trainingNumber: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  title: string;
  description: string;
  progress: number; // 0-100
  isLocked: boolean;
  isComplete: boolean;
  completedSections: number;
  totalSections: number;
  href: string;
}

export default function TrainingCard({
  trainingNumber,
  title,
  description,
  progress,
  isLocked,
  isComplete,
  completedSections,
  totalSections,
  href
}: TrainingCardProps) {
  // Determine button text and state
  const getButtonText = () => {
    if (isLocked) return 'Locked';
    if (isComplete) return 'Review';
    if (progress > 0) return 'Continue';
    return 'Start';
  };

  const buttonText = getButtonText();

  return (
    <Card
      className={`
        relative p-6 transition-all duration-300
        ${isLocked 
          ? 'opacity-60 cursor-not-allowed' 
          : 'hover:shadow-neon-cyan-intense cursor-pointer'
        }
        ${isComplete ? 'border-primary/50' : ''}
      `}
      hover={!isLocked}
    >
      {/* Lock overlay */}
      {isLocked && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
          <div className="text-center">
            <Icon 
              name="LockClosedIcon" 
              size={48} 
              className="mx-auto mb-3 text-muted-foreground"
            />
            <p className="text-sm font-semibold text-muted-foreground">
              {trainingNumber === 2 && 'Complete Training 1 to unlock'}
              {trainingNumber === 3 && 'Complete Training 2 to unlock'}
              {trainingNumber === 4 && 'Complete Training 3 to unlock'}
              {trainingNumber === 5 && 'Complete Training 4 to unlock'}
              {trainingNumber === 6 && 'Complete Training 5 to unlock'}
              {trainingNumber === 7 && 'Complete Training 6 to unlock'}
              {trainingNumber === 8 && 'Complete Training 7 to unlock'}
            </p>
          </div>
        </div>
      )}

      {/* Completion badge */}
      {isComplete && !isLocked && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-primary text-primary-foreground rounded-full p-2">
            <Icon name="CheckIcon" size={20} variant="solid" />
          </div>
        </div>
      )}

      {/* Card content */}
      <div className="relative">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className={`
              px-4 py-2 rounded-lg flex items-center justify-center font-semibold text-sm
              ${isLocked 
                ? 'bg-muted text-muted-foreground' 
                : isComplete
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'bg-primary/10 text-primary border border-primary/20'
              }
            `}>
              Training {trainingNumber}
            </div>
            <h3 className={`
              text-2xl font-semibold
              ${isLocked ? 'text-muted-foreground' : 'text-gradient-neon'}
            `}>
              {title}
            </h3>
          </div>
          <p className={`
            text-sm
            ${isLocked ? 'text-muted-foreground' : 'text-muted-foreground'}
          `}>
            {description}
          </p>
        </div>

        {/* Progress section */}
        {!isLocked && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">
                Progress
              </span>
              <span className="text-sm text-muted-foreground">
                {completedSections} / {totalSections} sections
              </span>
            </div>
            
            {/* Progress bar */}
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`
                  h-full transition-all duration-500 rounded-full
                  ${isComplete 
                    ? 'bg-primary' 
                    : 'bg-gradient-to-r from-primary to-secondary'
                  }
                `}
                style={{ width: `${progress}%` }}
              />
            </div>
            
            {/* Progress percentage */}
            <div className="mt-1 text-right">
              <span className="text-xs text-muted-foreground">
                {progress}% complete
              </span>
            </div>
          </div>
        )}

        {/* Action button */}
        <div className="mt-6">
          <Link
            href={href}
            className={`
              inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-sm
              font-semibold text-sm transition-all duration-200
              ${isLocked
                ? 'bg-muted text-muted-foreground cursor-not-allowed pointer-events-none'
                : isComplete
                  ? 'bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30'
                  : 'bg-primary text-primary-foreground hover:shadow-neon-cyan-intense'
              }
            `}
            onClick={(e) => {
              if (isLocked) {
                e.preventDefault();
              }
            }}
          >
            {!isLocked && (
              <>
                {isComplete ? (
                  <Icon name="ArrowPathIcon" size={18} />
                ) : progress > 0 ? (
                  <Icon name="PlayIcon" size={18} />
                ) : (
                  <Icon name="PlayIcon" size={18} />
                )}
              </>
            )}
            {isLocked && <Icon name="LockClosedIcon" size={18} />}
            <span>{buttonText}</span>
          </Link>
        </div>
      </div>
    </Card>
  );
}
